import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Users, UserCircle2, Skull, Castle, Scroll, Sparkles, Package, FileText, Map,
  BookOpen, Heart, Target, Flag, Layers, Wand2, Dices, ArrowRight, Table2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip HTML tags so rich-text bodies are searchable as plain text.
const plain = (s) => String(s || "").replace(/<[^>]*>/g, " ");

const NAV_PAGES = [
  { key: "chapters",          label: "Story",            icon: BookOpen,   group: "Navigate" },
  { key: "characters",        label: "Players",          icon: Users,      group: "Navigate" },
  { key: "characters?type=companion", label: "Companions", icon: Heart,    group: "Navigate" },
  { key: "npcs",              label: "NPCs",             icon: UserCircle2, group: "Navigate" },
  { key: "creatures",         label: "Creatures",        icon: Skull,      group: "Navigate" },
  { key: "items",             label: "Items",            icon: Package,    group: "Navigate" },
  { key: "lore",              label: "Lore",             icon: Scroll,     group: "Navigate" },
  { key: "deities",           label: "Deities",          icon: Sparkles,   group: "Navigate" },
  { key: "factions",          label: "Factions",         icon: Flag,       group: "Navigate" },
  { key: "quests",            label: "Quests",           icon: Target,     group: "Navigate" },
  { key: "maps",              label: "World Atlas",      icon: Map,        group: "Navigate" },
  { key: "locations",         label: "Locations",        icon: Castle,     group: "Navigate" },
  { key: "handouts",          label: "Handouts",         icon: FileText,   group: "Navigate" },
  { key: "vtt",               label: "Virtual Tabletop", icon: Layers,     group: "Navigate" },
  { key: "dm-screen",         label: "DM Screen",        icon: Dices,      group: "Navigate" },
  { key: "oracle",            label: "AI Oracle",        icon: Wand2,      group: "Navigate" },
];

const ENTITY_FETCHES = [
  { key: "characters",  group: "Players",    icon: Users,       endpoint: "characters",  routeKey: "characters", detailRoute: (id) => `characters/${id}/sheet` },
  { key: "npcs",        group: "NPCs",       icon: UserCircle2, endpoint: "npcs",        routeKey: "npcs" },
  { key: "creatures",   group: "Creatures",  icon: Skull,       endpoint: "creatures",   routeKey: "creatures" },
  { key: "items",       group: "Items",      icon: Package,     endpoint: "items",       routeKey: "items",      detailRoute: (id) => `item/${id}` },
  { key: "lore",        group: "Lore",       icon: Scroll,      endpoint: "lore",        routeKey: "lore",       text: (it) => plain(it.content) },
  { key: "locations",   group: "Locations",  icon: Castle,      endpoint: "locations",   routeKey: "locations",  text: (it) => plain(it.description) },
  { key: "chapters",    group: "Stories",    icon: BookOpen,    endpoint: "chapters",    routeKey: "chapters",   text: (it) => plain(it.content) },
  { key: "maps",        group: "Maps",       icon: Map,         endpoint: "maps",        routeKey: "maps" },
  // "Search everything" — also scan the body text of quests, session notes and table cells.
  { key: "quests",      group: "Quests",     icon: Target,      endpoint: "quests",      routeKey: "quests",     text: (it) => `${plain(it.description)} ${plain(it.dm_notes)} ${(it.objectives || []).map(o => o.text).join(" ")}` },
  { key: "sessions",    group: "Session Notes", icon: FileText, endpoint: "sessions",    routeKey: "chapters",   text: (it) => `${plain(it.content)} ${plain(it.recap)} ${(it.entries || []).map(e => e.content).join(" ")}` },
  { key: "tables",      group: "Tables",     icon: Table2,      endpoint: "tables",      routeKey: "tables",     detailRoute: (id) => `tables/${id}`, text: (it) => `${plain(it.description)} ${(it.columns || []).join(" ")} ${(it.rows || []).flat().join(" ")}` },
];

export const UniversalSearchDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset query when opening
  useEffect(() => { if (open) setQuery(""); }, [open]);

  // Fetch entities lazily once when the palette opens (cached for the session)
  useEffect(() => {
    if (!open || !campaignId) return;
    setLoading(true);
    Promise.allSettled(
      ENTITY_FETCHES.map(e =>
        axios.get(`${API}/campaigns/${campaignId}/${e.endpoint}`, { withCredentials: true })
      )
    ).then((res) => {
      const next = {};
      ENTITY_FETCHES.forEach((e, i) => {
        next[e.key] = res[i].status === "fulfilled" ? (res[i].value.data || []) : [];
      });
      setResults(next);
      setLoading(false);
    });
  }, [open, campaignId]);

  const filteredEntities = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ENTITY_FETCHES.map(e => {
      const items = (results[e.key] || []).filter(it => {
        if (!q) return false;
        const name = (it.name || it.title || "").toLowerCase();
        if (name.includes(q)) return true;
        // "Search everything": also match extra body text where defined.
        if (e.text) return e.text(it).toLowerCase().includes(q);
        return false;
      }).slice(0, 5);
      return { ...e, items };
    }).filter(g => g.items.length > 0);
  }, [results, query]);

  const go = useCallback((path) => {
    if (!campaignId) return;
    onOpenChange?.(false);
    navigate(`/campaign/${campaignId}/${path}`);
  }, [campaignId, navigate, onOpenChange]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search characters, NPCs, items, maps… or jump to any section"
        value={query}
        onValueChange={setQuery}
        data-testid="universal-search-input"
      />
      <CommandList data-testid="universal-search-list" className="max-h-[60vh]">
        <CommandEmpty>{loading ? "Searching…" : "No results."}</CommandEmpty>

        {/* Entity matches first (when typing) */}
        {filteredEntities.map(g => (
          <CommandGroup key={g.key} heading={g.group}>
            {g.items.map(it => {
              const Icon = g.icon;
              const id = it.id;
              const path = g.detailRoute ? g.detailRoute(id) : g.routeKey;
              return (
                <CommandItem
                  key={`${g.key}-${id}`}
                  value={`${g.group} ${it.name || it.title} ${g.text ? g.text(it) : ""}`}
                  onSelect={() => go(path)}
                  data-testid={`universal-search-result-${g.key}-${id}`}
                  className="cursor-pointer"
                >
                  <Icon className="w-4 h-4 mr-2 text-primary" />
                  <span className="flex-1">{it.name || it.title}</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground/50" />
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}

        {/* Always-on navigation shortcuts */}
        <CommandGroup heading="Navigate">
          {NAV_PAGES.map(p => {
            const Icon = p.icon;
            return (
              <CommandItem
                key={p.key}
                value={`navigate ${p.label}`}
                onSelect={() => go(p.key)}
                data-testid={`universal-search-nav-${p.key.replace(/[?=&/]/g, "-")}`}
                className="cursor-pointer"
              >
                <Icon className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="flex-1">{p.label}</span>
                <span className="text-[10px] text-muted-foreground/40">{p.key}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default UniversalSearchDialog;
